import React from 'react';
import { 
  ArrowLeft, 
  TrendingUp, 
  TrendingDown, 
  Users, 
  Target, 
  AlertTriangle,
  CheckCircle,
  Building2,
  Pickaxe,
  ArrowUpRight,
  ArrowDownRight,
  Minus,
  Info
} from 'lucide-react';
import { 
  BarChart, 
  Bar, 
  XAxis, 
  YAxis, 
  Tooltip, 
  ResponsiveContainer,
  Cell
} from 'recharts';
import { mockData } from '../../data/mockData';
import ExecutiveSummarySection from '../../components/ExecutiveSummarySection';
import { 
  getCurrentPeriodLabel, 
  getPreviousPeriodLabel, 
  getBudgetLabel,
  getTitleSuffix,
  getComparisonSubtitle
} from '../../utils/timeLabels';

// KPI Card Component with Trend Indicator - SCORECARD EXECUTIVO PATTERN
const KPICard = ({ title, value, unit, subValue, change, vsBudget, vsBudgetPp, isPositive, icon: Icon, ppChange }) => {
  // For cost metrics: decrease is positive (green), increase is negative (red)
  // Determine trend indicator based on YoY change or ppChange
  const numChange = parseFloat(change) || parseFloat(ppChange) || 0;
  const hasAnyChange = change !== undefined || ppChange !== undefined;
  
  // Semantic colors based on business logic (for costs, lower is better)
  const isCostFavorable = numChange <= 0;
  const trendBg = isCostFavorable ? 'bg-emerald-50' : 'bg-red-50';
  const trendColor = isCostFavorable ? '#0C8A4B' : '#C33D52';
  const TrendIcon = numChange > 0 ? ArrowUpRight : numChange < 0 ? ArrowDownRight : Minus;
  
  // Badge color logic for costs (inverse: lower is better)
  const getBudgetBadgeClass = (val) => {
    const numVal = parseFloat(val);
    if (numVal <= 0) return 'bg-emerald-100 text-emerald-700'; // Below budget = good
    if (numVal <= 5) return 'bg-amber-50 text-amber-700'; // Slightly above = warning
    return 'bg-rose-100 text-rose-700'; // Above budget = bad
  };
  
  // For YoY on costs: decrease (negative) = good (green), increase (positive) = bad (red)
  const getYoYBadgeClass = (val) => {
    const numVal = parseFloat(val);
    if (numVal <= 0) return 'bg-emerald-100 text-emerald-700'; // Decrease = good
    if (numVal <= 5) return 'bg-amber-50 text-amber-700'; // Slight increase = warning
    return 'bg-rose-100 text-rose-700'; // Increase = bad
  };
  
  return (
    <div className="bg-white rounded-xl border border-slate-200 p-5 hover:shadow-lg hover:border-gold-300/50 transition-all duration-300">
      {/* SCORECARD LAYOUT: Content on left, Trend Icon on right */}
      <div className="flex items-start justify-between">
        <div className="flex-1 min-w-0">
          <p className="text-xs text-slate-500 tracking-wide mb-1.5">{title}</p>
          <div className="flex items-baseline gap-1.5 mb-2">
            <span className="font-sans text-2xl font-semibold tracking-[-0.3px] text-primary">{value}</span>
            <span className="text-sm text-slate-400">{unit}</span>
          </div>
          {subValue && (
            <p className="text-xs text-slate-400 mb-2">{subValue}</p>
          )}
          {/* Comparison Badges - SCORECARD PATTERN with backgrounds */}
          <div className="flex flex-wrap items-center gap-2 mt-2">
            {vsBudget !== undefined && vsBudget !== null && (
              <span className={`inline-flex items-center gap-1 px-1.5 py-0.5 rounded text-[11px] font-semibold ${getBudgetBadgeClass(vsBudget)}`}>
                {parseFloat(vsBudget) <= 0 ? <TrendingDown className="w-3 h-3" /> : <TrendingUp className="w-3 h-3" />}
                {parseFloat(vsBudget) > 0 ? '+' : ''}{vsBudget}% Orç.
              </span>
            )}
            {vsBudgetPp !== undefined && vsBudgetPp !== null && (
              <span className={`inline-flex items-center gap-1 px-1.5 py-0.5 rounded text-[11px] font-semibold ${parseFloat(vsBudgetPp) <= 0 ? 'bg-emerald-100 text-emerald-700' : 'bg-amber-50 text-amber-700'}`}>
                {parseFloat(vsBudgetPp) > 0 ? '+' : ''}{vsBudgetPp}p.p. vs Orç.
              </span>
            )}
            {change !== undefined && change !== null && (
              <span className={`inline-flex items-center gap-1 px-1.5 py-0.5 rounded text-[11px] font-semibold ${getYoYBadgeClass(change)}`}>
                {parseFloat(change) <= 0 ? <TrendingDown className="w-3 h-3" /> : <TrendingUp className="w-3 h-3" />}
                {parseFloat(change) > 0 ? '+' : ''}{change}% YoY
              </span>
            )}
            {ppChange !== undefined && ppChange !== null && (
              <span className={`inline-flex items-center gap-1 px-1.5 py-0.5 rounded text-[11px] font-semibold ${parseFloat(ppChange) <= 0 ? 'bg-emerald-100 text-emerald-700' : 'bg-amber-50 text-amber-700'}`}>
                {parseFloat(ppChange) > 0 ? '+' : ''}{ppChange} pp YoY
              </span>
            )}
          </div>
        </div>
        {/* Right side: Circular Trend Icon - SCORECARD PATTERN */}
        {hasAnyChange && (
          <div 
            className="w-10 h-10 rounded-full flex items-center justify-center flex-shrink-0 ml-3"
            style={{ backgroundColor: trendBg.replace('bg-', '').includes('emerald') ? '#EAF7EF' : '#FBECEF' }}
          >
            <TrendIcon className="w-5 h-5" style={{ color: trendColor }} />
          </div>
        )}
      </div>
    </div>
  );
};

// Comparison Card Component (Premium visual for Man Power vs Sondagem)
const ComparisonCard = ({ title, data, prevYearData, language, accentColor, vsBudget, budgetValue, quarterlyData }) => {
  const isGrowing = data.yoyChange > 0;
  const budgetVar = vsBudget || data.vsBudget || 0;
  const budget = budgetValue || data.budgetValue || (data.value / (1 + budgetVar/100));
  
  // Quarterly data for mini chart
  const chartData = quarterlyData || [
    { quarter: 'Q1', y2025: data.value * 0.22, budget2025: budget * 0.24, y2024: (prevYearData?.value || data.value * 0.9) * 0.23 },
    { quarter: 'Q2', y2025: data.value * 0.26, budget2025: budget * 0.25, y2024: (prevYearData?.value || data.value * 0.9) * 0.25 },
    { quarter: 'Q3', y2025: data.value * 0.28, budget2025: budget * 0.26, y2024: (prevYearData?.value || data.value * 0.9) * 0.26 },
    { quarter: 'Q4', y2025: data.value * 0.24, budget2025: budget * 0.25, y2024: (prevYearData?.value || data.value * 0.9) * 0.26 },
  ];
  
  // Calculate comparison metrics
  const q2025Values = chartData.map(q => q.y2025);
  const q2024Values = chartData.map(q => q.y2024);
  
  // Find quarter with highest cost and its YoY comparison
  const maxQ2025Idx = q2025Values.indexOf(Math.max(...q2025Values));
  const maxQuarter = chartData[maxQ2025Idx].quarter;
  const maxQ2025 = q2025Values[maxQ2025Idx];
  const maxQ2024 = q2024Values[maxQ2025Idx];
  const maxQChange = ((maxQ2025 - maxQ2024) / maxQ2024 * 100);
  
  // Monthly data simulation (based on quarterly distribution)
  const monthlyMultipliers = [0.30, 0.32, 0.38, 0.31, 0.33, 0.36, 0.32, 0.34, 0.34, 0.33, 0.35, 0.32];
  const monthly2025 = monthlyMultipliers.map((m, i) => data.value * m / 3);
  const monthly2024 = monthlyMultipliers.map((m, i) => (prevYearData?.value || data.value * 0.9) * m / 3);
  const maxMonthIdx = monthly2025.indexOf(Math.max(...monthly2025));
  const monthNames = language === 'pt' 
    ? ['Jan', 'Fev', 'Mar', 'Abr', 'Mai', 'Jun', 'Jul', 'Ago', 'Set', 'Out', 'Nov', 'Dez']
    : ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'];
  const maxMonth = monthNames[maxMonthIdx];
  const maxMonthChange = ((monthly2025[maxMonthIdx] - monthly2024[maxMonthIdx]) / monthly2024[maxMonthIdx] * 100);
  
  // Calculate mean and median
  const mean2025 = q2025Values.reduce((a, b) => a + b, 0) / q2025Values.length;
  const mean2024 = q2024Values.reduce((a, b) => a + b, 0) / q2024Values.length;
  const meanChange = ((mean2025 - mean2024) / mean2024 * 100);
  
  const sorted2025 = [...q2025Values].sort((a, b) => a - b);
  const sorted2024 = [...q2024Values].sort((a, b) => a - b);
  const median2025 = (sorted2025[1] + sorted2025[2]) / 2;
  const median2024 = (sorted2024[1] + sorted2024[2]) / 2;
  const medianChange = ((median2025 - median2024) / median2024 * 100);

  // Custom tooltip for the chart
  const CustomTooltip = ({ active, payload, label }) => {
    if (active && payload && payload.length) {
      return (
        <div className="bg-white border border-slate-200 rounded-lg shadow-lg p-2 text-xs">
          <p className="font-semibold text-slate-700 mb-1">{label}</p>
          {payload.map((entry, index) => (
            <p key={index} style={{ color: entry.color }} className="flex justify-between gap-3">
              <span>{entry.name}:</span>
              <span className="font-semibold">{entry.value.toFixed(1)}M</span>
            </p>
          ))}
        </div>
      );
    }
    return null;
  };
  
  return (
    <div className={`bg-white rounded-2xl border-2 ${accentColor === 'blue' ? 'border-blue-200' : 'border-amber-200'} p-6 relative overflow-hidden`}>
      {/* Accent gradient */}
      <div className={`absolute top-0 left-0 right-0 h-1.5 ${accentColor === 'blue' ? 'bg-gradient-to-r from-blue-400 to-blue-600' : 'bg-gradient-to-r from-amber-400 to-amber-600'}`}></div>
      
      {/* Header */}
      <div className="flex items-center justify-between mb-4">
        <div className="flex items-center gap-3">
          <div className={`w-12 h-12 rounded-xl flex items-center justify-center ${accentColor === 'blue' ? 'bg-blue-100' : 'bg-amber-100'}`}>
            {accentColor === 'blue' ? (
              <Users className={`w-6 h-6 ${accentColor === 'blue' ? 'text-blue-600' : 'text-amber-600'}`} />
            ) : (
              <Pickaxe className={`w-6 h-6 ${accentColor === 'blue' ? 'text-blue-600' : 'text-amber-600'}`} />
            )}
          </div>
          <h3 className="text-lg font-bold text-primary">{title}</h3>
        </div>
      </div>
      
      {/* Main Value */}
      <div className="mb-6">
        <div className="flex items-baseline gap-2">
          <span className="text-4xl font-bold text-primary">{data.value.toFixed(1)}</span>
          <span className="text-lg text-slate-500">M USD</span>
        </div>
        <p className="text-sm text-slate-500 mt-1">
          {data.percentage.toFixed(0)}% {language === 'pt' ? 'do total' : 'of total'}
        </p>
      </div>
      
      {/* Metrics Grid - Compact version */}
      <div className="grid grid-cols-4 gap-2">
        {/* vs Orçamento */}
        <div className="bg-slate-50 rounded-lg p-2">
          <p className="text-[10px] text-slate-500 mb-0.5">{language === 'pt' ? 'vs Orçamento' : 'vs Budget'}</p>
          <div className={`flex items-center gap-1 font-semibold text-sm ${budgetVar > 0 ? 'text-red-600' : 'text-emerald-600'}`}>
            {budgetVar > 0 ? <TrendingUp className="w-3 h-3" /> : <TrendingDown className="w-3 h-3" />}
            <span>{budgetVar > 0 ? '+' : ''}{budgetVar.toFixed(1)}%</span>
          </div>
        </div>
        
        {/* Orçamento 2025 */}
        <div className="bg-slate-50 rounded-lg p-2">
          <p className="text-[10px] text-slate-500 mb-0.5">{language === 'pt' ? 'Orç. 2025' : 'Bdgt 2025'}</p>
          <span className="font-semibold text-sm text-slate-700">{budget.toFixed(1)} M</span>
        </div>
        
        {/* YoY */}
        <div className="bg-slate-50 rounded-lg p-2">
          <p className="text-[10px] text-slate-500 mb-0.5">YoY</p>
          <div className={`flex items-center gap-1 font-semibold text-sm ${isGrowing ? 'text-red-600' : 'text-emerald-600'}`}>
            {isGrowing ? <TrendingUp className="w-3 h-3" /> : <TrendingDown className="w-3 h-3" />}
            <span>{data.yoyChange > 0 ? '+' : ''}{data.yoyChange?.toFixed(1) || 'N/A'}%</span>
          </div>
        </div>
        
        {/* 2024 Value */}
        <div className="bg-slate-50 rounded-lg p-2">
          <p className="text-[10px] text-slate-500 mb-0.5">2024</p>
          <span className="font-semibold text-sm text-slate-700">{prevYearData?.value?.toFixed(1) || 'N/A'} M</span>
        </div>
      </div>

      {/* Mini Quarterly Chart - Larger */}
      <div className="mt-4 pt-3 border-t border-slate-100">
        <div className="flex items-center justify-between mb-2">
          <p className="text-xs text-slate-500 font-medium">{language === 'pt' ? 'Evolução Trimestral' : 'Quarterly Evolution'}</p>
          <div className="flex items-center gap-3 text-[10px]">
            <span className="flex items-center gap-1">
              <span className={`w-2 h-2 rounded-full ${accentColor === 'blue' ? 'bg-blue-500' : 'bg-amber-500'}`}></span>
              <span className="text-slate-500">2025</span>
            </span>
            <span className="flex items-center gap-1">
              <span className="w-2 h-2 rounded-full bg-slate-300"></span>
              <span className="text-slate-500">{language === 'pt' ? 'Orç.' : 'Bdgt'}</span>
            </span>
            <span className="flex items-center gap-1">
              <span className="w-2 h-2 rounded-full bg-slate-400"></span>
              <span className="text-slate-500">2024</span>
            </span>
          </div>
        </div>
        <div className="h-[120px]">
          <ResponsiveContainer width="100%" height="100%">
            <BarChart data={chartData} barGap={1} barCategoryGap="15%">
              <XAxis 
                dataKey="quarter" 
                axisLine={false}
                tickLine={false}
                tick={{ fontSize: 10, fill: '#94a3b8' }}
              />
              <YAxis hide />
              <Tooltip content={<CustomTooltip />} cursor={{ fill: 'rgba(0,0,0,0.03)' }} />
              <Bar 
                dataKey="y2025" 
                name="2025"
                radius={[3, 3, 0, 0]}
                fill={accentColor === 'blue' ? '#3b82f6' : '#f59e0b'}
              />
              <Bar 
                dataKey="budget2025" 
                name={language === 'pt' ? 'Orç. 2025' : 'Bdgt 2025'}
                radius={[3, 3, 0, 0]}
                fill="#cbd5e1"
              />
              <Bar 
                dataKey="y2024" 
                name="2024"
                radius={[3, 3, 0, 0]}
                fill="#94a3b8"
              />
            </BarChart>
          </ResponsiveContainer>
        </div>
      </div>
      
      {/* Comparative Metrics - replacing Unit Cost */}
      <div className="grid grid-cols-2 gap-2 mt-3">
        {/* Trimestre com maior custo */}
        <div className="bg-slate-50 rounded-lg p-2">
          <p className="text-[10px] text-slate-500 mb-0.5">{language === 'pt' ? 'Trim. Maior Custo' : 'Highest Quarter'}</p>
          <div className="flex items-center justify-between">
            <span className="font-semibold text-sm text-slate-700">{maxQuarter}</span>
            <span className={`text-[10px] font-semibold ${maxQChange > 0 ? 'text-red-600' : 'text-emerald-600'}`}>
              {maxQChange > 0 ? '+' : ''}{maxQChange.toFixed(1)}% YoY
            </span>
          </div>
        </div>
        
        {/* Mês com maior custo */}
        <div className="bg-slate-50 rounded-lg p-2">
          <p className="text-[10px] text-slate-500 mb-0.5">{language === 'pt' ? 'Mês Maior Custo' : 'Highest Month'}</p>
          <div className="flex items-center justify-between">
            <span className="font-semibold text-sm text-slate-700">{maxMonth}</span>
            <span className={`text-[10px] font-semibold ${maxMonthChange > 0 ? 'text-red-600' : 'text-emerald-600'}`}>
              {maxMonthChange > 0 ? '+' : ''}{maxMonthChange.toFixed(1)}% YoY
            </span>
          </div>
        </div>
        
        {/* Média trimestral */}
        <div className="bg-slate-50 rounded-lg p-2">
          <p className="text-[10px] text-slate-500 mb-0.5">{language === 'pt' ? 'Média Trim.' : 'Qtr Mean'}</p>
          <div className="flex items-center justify-between">
            <span className="font-semibold text-sm text-slate-700">{mean2025.toFixed(1)}M</span>
            <span className={`text-[10px] font-semibold ${meanChange > 0 ? 'text-red-600' : 'text-emerald-600'}`}>
              {meanChange > 0 ? '+' : ''}{meanChange.toFixed(1)}% YoY
            </span>
          </div>
        </div>
        
        {/* Mediana trimestral */}
        <div className="bg-slate-50 rounded-lg p-2">
          <p className="text-[10px] text-slate-500 mb-0.5">{language === 'pt' ? 'Mediana Trim.' : 'Qtr Median'}</p>
          <div className="flex items-center justify-between">
            <span className="font-semibold text-sm text-slate-700">{median2025.toFixed(1)}M</span>
            <span className={`text-[10px] font-semibold ${medianChange > 0 ? 'text-red-600' : 'text-emerald-600'}`}>
              {medianChange > 0 ? '+' : ''}{medianChange.toFixed(1)}% YoY
            </span>
          </div>
        </div>
      </div>
    </div>
  );
};

// Dumbbell Chart Component
const DumbbellChart = ({ data, prevYearData, language }) => {
  const maxValue = Math.max(
    data.manPower.value, 
    data.sondagem.value, 
    prevYearData?.manPower?.value || 0, 
    prevYearData?.sondagem?.value || 0
  ) * 1.2;
  
  const getPosition = (value) => ((value / maxValue) * 100);
  
  return (
    <div className="bg-white rounded-xl border border-slate-200 p-6">
      <div className="flex items-center justify-between mb-6">
        <h3 className="font-serif text-lg font-semibold text-primary">
          {language === 'pt' ? 'Evolução 2024 vs 2025' : '2024 vs 2025 Evolution'}
        </h3>
        <div className="flex items-center gap-4 text-xs">
          <div className="flex items-center gap-1.5">
            <div className="w-3 h-3 rounded-full bg-slate-400"></div>
            <span className="text-slate-500">2024</span>
          </div>
          <div className="flex items-center gap-1.5">
            <div className="w-3 h-3 rounded-full bg-primary"></div>
            <span className="text-slate-500">2025</span>
          </div>
        </div>
      </div>
      
      <div className="space-y-8">
        {/* Man Power */}
        <div>
          <div className="flex items-center justify-between mb-2">
            <div className="flex items-center gap-2">
              <Users className="w-4 h-4 text-blue-600" />
              <span className="font-semibold text-primary">Man Power</span>
            </div>
            <span className={`text-xs px-2 py-0.5 rounded-full font-medium ${data.manPower.yoyChange > 0 ? 'bg-rose-100 text-rose-700' : 'bg-emerald-100 text-emerald-700'}`}>
              {data.manPower.yoyChange > 0 ? '+' : ''}{data.manPower.yoyChange?.toFixed(1) || 0}% YoY
            </span>
          </div>
          <div className="relative h-10 bg-slate-100 rounded-full">
            {/* Connecting line */}
            <div 
              className="absolute top-1/2 h-1 bg-gradient-to-r from-slate-400 to-blue-600 rounded-full transform -translate-y-1/2"
              style={{ 
                left: `${getPosition(prevYearData?.manPower?.value || 0)}%`, 
                width: `${Math.abs(getPosition(data.manPower.value) - getPosition(prevYearData?.manPower?.value || 0))}%` 
              }}
            />
            {/* 2024 dot */}
            <div 
              className="absolute top-1/2 w-6 h-6 rounded-full bg-slate-400 border-2 border-white shadow-md transform -translate-y-1/2 -translate-x-1/2 flex items-center justify-center"
              style={{ left: `${getPosition(prevYearData?.manPower?.value || 0)}%` }}
            >
              <span className="text-[8px] font-bold text-white">{(prevYearData?.manPower?.value || 0).toFixed(0)}</span>
            </div>
            {/* 2025 dot */}
            <div 
              className="absolute top-1/2 w-8 h-8 rounded-full bg-blue-600 border-2 border-white shadow-lg transform -translate-y-1/2 -translate-x-1/2 flex items-center justify-center"
              style={{ left: `${getPosition(data.manPower.value)}%` }}
            >
              <span className="text-[10px] font-bold text-white">{data.manPower.value.toFixed(1)}</span>
            </div>
          </div>
        </div>
        
        {/* Sondagem */}
        <div>
          <div className="flex items-center justify-between mb-2">
            <div className="flex items-center gap-2">
              <Pickaxe className="w-4 h-4 text-amber-600" />
              <span className="font-semibold text-primary">{language === 'pt' ? 'Sondagem' : 'Drilling'}</span>
            </div>
            <span className={`text-xs px-2 py-0.5 rounded-full font-medium ${data.sondagem.yoyChange > 0 ? 'bg-rose-100 text-rose-700' : 'bg-emerald-100 text-emerald-700'}`}>
              {data.sondagem.yoyChange > 0 ? '+' : ''}{data.sondagem.yoyChange?.toFixed(1) || 0}% YoY
            </span>
          </div>
          <div className="relative h-10 bg-slate-100 rounded-full">
            {/* Connecting line */}
            <div 
              className="absolute top-1/2 h-1 bg-gradient-to-r from-slate-400 to-amber-600 rounded-full transform -translate-y-1/2"
              style={{ 
                left: `${getPosition(prevYearData?.sondagem?.value || 0)}%`, 
                width: `${Math.abs(getPosition(data.sondagem.value) - getPosition(prevYearData?.sondagem?.value || 0))}%` 
              }}
            />
            {/* 2024 dot */}
            <div 
              className="absolute top-1/2 w-6 h-6 rounded-full bg-slate-400 border-2 border-white shadow-md transform -translate-y-1/2 -translate-x-1/2 flex items-center justify-center"
              style={{ left: `${getPosition(prevYearData?.sondagem?.value || 0)}%` }}
            >
              <span className="text-[8px] font-bold text-white">{(prevYearData?.sondagem?.value || 0).toFixed(0)}</span>
            </div>
            {/* 2025 dot */}
            <div 
              className="absolute top-1/2 w-8 h-8 rounded-full bg-amber-600 border-2 border-white shadow-lg transform -translate-y-1/2 -translate-x-1/2 flex items-center justify-center"
              style={{ left: `${getPosition(data.sondagem.value)}%` }}
            >
              <span className="text-[10px] font-bold text-white">{data.sondagem.value.toFixed(1)}</span>
            </div>
          </div>
        </div>
      </div>
      
      {/* Scale */}
      <div className="flex justify-between mt-4 text-xs text-slate-400">
        <span>0</span>
        <span>{(maxValue / 2).toFixed(0)} M USD</span>
        <span>{maxValue.toFixed(0)} M USD</span>
      </div>
    </div>
  );
};

const SubcontractsAnalysis = ({ language, year, onBack, flowMap }) => {
  const subcontractsData = mockData.subcontracts?.[year] || mockData.subcontracts?.[2025];
  const prevYearData = mockData.subcontracts?.[year - 1] || mockData.subcontracts?.[2024];
  
  // Dynamic time labels based on year (quarterly format for 2026)
  const currentPeriod = getCurrentPeriodLabel(year, language);
  const previousPeriod = getPreviousPeriodLabel(year, language);
  const budgetLabelFull = getBudgetLabel(year, language);
  const titleSuffix = getTitleSuffix(year, language);
  const comparisonSubtitle = getComparisonSubtitle(year, language);
  
  // Budget data for subcontracts
  const budgetSubcontracts = subcontractsData?.totalCost ? subcontractsData.totalCost * 0.95 : 0;
  const subcontractsVsBudget = budgetSubcontracts ? ((subcontractsData.totalCost - budgetSubcontracts) / budgetSubcontracts * 100).toFixed(1) : '0';
  
  // Calculate weight in OPEX pp change
  const weightInOpexPpChange = prevYearData?.weightInOpex 
    ? (subcontractsData.weightInOpex - prevYearData.weightInOpex).toFixed(1)
    : null;

  // Calculate Subcontracts per carat (regular carats recovered)
  const costPerCaratData = mockData.costPerCarat?.[year] || {};
  const prevCostPerCaratData = mockData.costPerCarat?.[year - 1] || {};
  const regularCarats = costPerCaratData.kpis?.regularCarats?.value || 920;
  const prevRegularCarats = prevCostPerCaratData.kpis?.regularCarats?.value || 820;
  
  const subPerCarat = subcontractsData && regularCarats > 0 ? Math.round((subcontractsData.totalCost * 1000) / regularCarats) : 0;
  const prevSubPerCarat = prevYearData && prevRegularCarats > 0 ? Math.round((prevYearData.totalCost * 1000) / prevRegularCarats) : 0;
  const subPerCaratChange = prevSubPerCarat > 0 ? ((subPerCarat - prevSubPerCarat) / prevSubPerCarat * 100).toFixed(1) : '0';

  if (!subcontractsData) {
    return (
      <div className="space-y-6">
        <div className="bg-white rounded-xl border border-slate-200 p-8 text-center">
          <Building2 className="w-16 h-16 text-slate-300 mx-auto mb-4" />
          <h3 className="text-xl font-semibold text-primary mb-2">
            {language === 'pt' ? 'Dados não disponíveis' : 'Data not available'}
          </h3>
        </div>
      </div>
    );
  }

  return (
    <div className="space-y-6" data-testid="subcontracts-analysis-page">
      {/* Header */}
      <div>
        <button 
          onClick={onBack} 
          className="flex items-center gap-2 text-sm mb-4 transition-colors hover:opacity-80"
          style={{ color: '#7A859D' }}
          data-testid="back-to-opex-btn"
        >
          <ArrowLeft className="w-4 h-4" />
          {language === 'pt' ? 'Voltar a Custos Operacionais' : 'Back to Operating Costs'}
        </button>
        <div className="flex items-start justify-between">
          <div>
            <h1 
              className="text-[38px] leading-[44px] font-semibold tracking-[-0.3px] mb-2"
              style={{ color: '#0E1633', fontFamily: '"Source Serif 4", serif' }}
            >
              {language === 'pt' ? 'Subcontratos' : 'Subcontracts'} | {titleSuffix}
            </h1>
            <p 
              className="text-[15px] leading-[22px]"
              style={{ color: '#425070' }}
            >
              {comparisonSubtitle}
            </p>
          </div>
          
        </div>
      </div>

      {/* Key Insights - Horizontal Block (same style as CMVMC) */}
      <div className="bg-gradient-to-r from-[#0A192F] to-[#1a2744] rounded-2xl p-6">
        <div className="flex items-center gap-2 mb-5">
          <div className="w-1 h-5 bg-gold-500 rounded-full"></div>
          <h3 className="text-gold-400 font-semibold text-sm uppercase tracking-wider">
            {language === 'pt' ? 'Mensagens-Chave' : 'Key Insights'}
          </h3>
        </div>
        
        <div className="grid grid-cols-3 gap-5">
          {[
            {
              driver: { pt: 'Concentração', en: 'Concentration' },
              detail: { pt: 'Man Power é o principal componente, condiciona flexibilidade.', en: 'Man Power is the main component, limits flexibility.' },
              direction: 'up',
              impact: 'high',
            },
            {
              driver: { pt: 'Volatilidade', en: 'Volatility' },
              detail: { pt: 'Sondagem com alta sensibilidade a campanhas e necessidades.', en: 'Drilling with high sensitivity to campaigns and needs.' },
              direction: 'neutral',
              impact: 'medium',
            },
            {
              driver: { pt: 'Disciplina', en: 'Discipline' },
              detail: { pt: 'Separar custo estrutural de pontual, controlo de escopo.', en: 'Separate structural from one-off cost, scope control.' },
              direction: 'down',
              impact: 'low',
            },
          ].map((insight, idx) => {
            const driverName = typeof insight.driver === 'object' ? insight.driver[language] : insight.driver;
            const detail = typeof insight.detail === 'object' ? insight.detail[language] : insight.detail;
            const isUp = insight.direction === 'up';
            const isNeutral = insight.direction === 'neutral';
            const impactBg = insight.impact === 'high' ? 'bg-red-500/20' : insight.impact === 'medium' ? 'bg-amber-500/20' : 'bg-blue-500/20';
            const impactBorder = insight.impact === 'high' ? 'border-red-500/30' : insight.impact === 'medium' ? 'border-amber-500/30' : 'border-blue-500/30';
            
            return (
              <div 
                key={idx} 
                className={`${impactBg} ${impactBorder} border rounded-xl p-4 transition-all duration-300 hover:scale-[1.02]`}
              >
                <div className="flex items-start gap-3">
                  <div className={`w-10 h-10 rounded-xl flex items-center justify-center flex-shrink-0 ${
                    isNeutral ? 'bg-amber-500/30' : isUp ? 'bg-red-500/30' : 'bg-emerald-500/30'
                  }`}>
                    {isNeutral ? (
                      <AlertTriangle className="w-5 h-5 text-amber-400" />
                    ) : isUp ? (
                      <TrendingUp className="w-5 h-5 text-red-400" />
                    ) : (
                      <TrendingDown className="w-5 h-5 text-emerald-400" />
                    )}
                  </div>
                  <div className="flex-1 min-w-0">
                    <h4 className="font-semibold text-white text-sm mb-1.5 leading-tight">{driverName}</h4>
                    <p className="text-slate-400 text-xs leading-relaxed">{detail}</p>
                  </div>
                </div>
              </div>
            );
          })}
        </div>
      </div>

      {/* KPI Cards */}
      <div className="grid grid-cols-4 gap-4">
        <KPICard 
          title={language === 'pt' ? 'Subcontratos' : 'Subcontracts'}
          value={subcontractsData.totalCost}
          unit="M USD"
          change={subcontractsData.yoyChange}
          vsBudget={subcontractsVsBudget}
          isPositive={subcontractsData.yoyChange < 0}
          icon={Building2}
        />
        <KPICard 
          title={language === 'pt' ? 'Peso nos Custos Operacionais' : 'Weight in Operating Costs'}
          value={subcontractsData.weightInOpex}
          unit="%"
          ppChange={weightInOpexPpChange}
          vsBudgetPp="+0.4"
          icon={Target}
        />
        <KPICard 
          title="Man Power"
          value={subcontractsData.manPower.value}
          unit="M USD"
          subValue={`${subcontractsData.manPower.percentage.toFixed(0)}% ${language === 'pt' ? 'do total' : 'of total'}`}
          vsBudget="+4.8"
          change={subcontractsData.manPower.yoyChange}
          isPositive={subcontractsData.manPower.yoyChange < 0}
          icon={Users}
        />
        <KPICard 
          title={language === 'pt' ? 'Sondagem' : 'Drilling'}
          value={subcontractsData.sondagem.value}
          unit="M USD"
          subValue={`${subcontractsData.sondagem.percentage.toFixed(0)}% ${language === 'pt' ? 'do total' : 'of total'}`}
          vsBudget="+6.2"
          change={subcontractsData.sondagem.yoyChange}
          isPositive={subcontractsData.sondagem.yoyChange < 0}
          icon={Pickaxe}
        />
      </div>

      {/* Sumário Executivo - Executive Summary - Following CMVMC style */}
      <ExecutiveSummarySection
        title={language === 'pt' ? 'Sumário Executivo' : 'Executive Summary'}
        subtitle={language === 'pt' ? 'Subcontratos e serviços externos' : 'Subcontracts and external services'}
        language={language}
        maxHeight="500px"
        icon={Info}
        dataTestId="subcontracts-executive-summary"
      >
        {/* Visão Global */}
        <div className="mb-6">
          <h4 className="font-semibold text-primary text-sm mb-2">{language === 'pt' ? 'Visão Global' : 'Overview'}</h4>
          <p className="text-slate-700 leading-relaxed text-sm mb-3">
            {language === 'pt' 
              ? 'Em 2025, Subcontratos encerrou em USD 19,5M, +5,3% Orçamento e +9,6% vs 2024, passando a representar 10,0% dos custos operacionais. O desvio não foi homogéneo entre componentes: Man Power explicou a maior parte da pressão em valor absoluto, enquanto Sondagem apresentou a maior pressão relativa face ao plano. Em termos de leitura financeira, a rubrica mostra um reforço do recurso a capacidade externa acima do previsto e acima do nível do período homólogo, com maior peso na estrutura de custo operacional.'
              : 'In 2025, Subcontracts closed at USD 19.5M, +5.3% Budget and +9.6% vs 2024, now representing 10.0% of operating costs. The deviation was not homogeneous: Man Power explained most of the pressure in absolute value, while Drilling showed the highest relative pressure vs plan.'}
          </p>
          <p className="text-slate-700 leading-relaxed text-sm">
            {language === 'pt' 
              ? 'O ponto central nesta secção é distinguir duas naturezas de custo muito diferentes. Man Power funciona como base de flexibilidade operacional e tende a ter comportamento mais recorrente ao longo do ano. Sondagem tem perfil mais sensível a campanhas, calendário técnico e necessidades específicas de exploração, o que explica maior volatilidade intra-anual. Esta distinção é relevante porque o desvio da rubrica total resulta da soma de um bloco mais estrutural com outro mais táctico. Serviços de suporte externos, incluindo sondagens, exploração, engenharia e projectos, são tratados como capacidade especializada de apoio directo à operação.'
              : 'The central point in this section is distinguishing two very different cost natures. Man Power functions as operational flexibility base with more recurrent behavior. Drilling has a profile more sensitive to campaigns, technical calendar and specific exploration needs, explaining higher intra-annual volatility.'}
          </p>
        </div>

        {/* Driver Sections - Following CMVMC style */}
        <div className="space-y-4">
          {/* Man Power */}
          <div className="bg-gradient-to-r from-blue-50 to-slate-50 rounded-xl p-5 border border-blue-100">
            <div className="flex items-center gap-3 mb-3">
              <div className="w-10 h-10 rounded-xl bg-blue-100 flex items-center justify-center">
                <Users className="w-5 h-5 text-blue-600" />
              </div>
              <div>
                <h4 className="font-semibold text-primary text-sm">Man Power</h4>
                <p className="text-xs text-slate-500">{language === 'pt' ? '64% do total de Subcontratos' : '64% of total Subcontracts'}</p>
              </div>
              <div className="ml-auto flex items-center gap-2">
                <span className="text-lg font-bold text-primary">12.4</span>
                <span className="text-xs text-slate-500">M USD</span>
              </div>
            </div>
            <p className="text-slate-600 text-xs leading-relaxed mb-3">
              {language === 'pt' 
                ? 'Man Power fechou o ano em USD 12,4M, equivalente a 64% do total de Subcontratos, com desvio de +4,8% Orçamento e +10,7% vs 2024. Sendo a componente dominante da rubrica, foi também o principal driver do desvio total em valor absoluto.'
                : 'Man Power closed the year at USD 12.4M, equivalent to 64% of total Subcontracts, with +4.8% Budget and +10.7% vs 2024 deviation. Being the dominant component, it was also the main driver of total deviation in absolute value.'}
            </p>
            <p className="text-slate-600 text-xs leading-relaxed mb-3">
              {language === 'pt' 
                ? 'Face ao orçamento, a leitura mais provável é de maior utilização de capacidade externa do que a inicialmente prevista, seja por necessidade de reforço de execução no terreno, seja por substituição parcial de capacidade interna, seja por necessidade de manter continuidade operacional sem alargar estrutura fixa na mesma proporção. Face a 2024, o aumento sugere que o recurso a Man Power deixou de ser apenas absorção pontual de picos e passou a ter maior presença ao longo do ano.'
                : 'Against budget, the most likely reading is higher use of external capacity than initially planned, whether due to field execution reinforcement needs, partial substitution of internal capacity, or need to maintain operational continuity without expanding fixed structure proportionally.'}
            </p>
            <p className="text-slate-600 text-xs leading-relaxed mb-3">
              {language === 'pt' 
                ? 'O perfil temporal reforça essa leitura. O 3.º trimestre foi o de maior custo e a média trimestral ficou praticamente alinhada com a mediana, o que indica uma rubrica com pressão distribuída ao longo do ano e não apenas concentrada em eventos isolados. Para a gerência, isto significa que a análise seguinte deve confirmar se esta linha está a financiar flexibilidade operacional necessária ou se começa a traduzir dependência recorrente de capacidade externa.'
                : 'The temporal profile reinforces this reading. Q3 had the highest cost and quarterly mean was practically aligned with median, indicating distributed pressure throughout the year, not concentrated in isolated events.'}
            </p>
            <div className="bg-white/60 rounded-lg p-3 border border-blue-100">
              <p className="text-xs font-semibold text-slate-700 mb-2">{language === 'pt' ? 'Leitura de Gestão' : 'Management Reading'}</p>
              <ul className="text-xs text-slate-600 space-y-1">
                <li className="flex items-center gap-2">
                  <div className="w-1.5 h-1.5 rounded-full bg-blue-500"></div>
                  {language === 'pt' ? 'Principal driver do desvio total em valor absoluto' : 'Main driver of total deviation in absolute value'}
                </li>
                <li className="flex items-center gap-2">
                  <div className="w-1.5 h-1.5 rounded-full bg-blue-500"></div>
                  {language === 'pt' ? 'Perfil recorrente ao longo do ano (média ≈ mediana)' : 'Recurrent profile throughout the year (mean ≈ median)'}
                </li>
                <li className="flex items-center gap-2">
                  <div className="w-1.5 h-1.5 rounded-full bg-blue-500"></div>
                  {language === 'pt' ? 'Validar se financia flexibilidade necessária ou dependência externa' : 'Validate if financing necessary flexibility or external dependency'}
                </li>
              </ul>
            </div>
          </div>

          {/* Sondagem */}
          <div className="bg-gradient-to-r from-amber-50 to-slate-50 rounded-xl p-5 border border-amber-100">
            <div className="flex items-center gap-3 mb-3">
              <div className="w-10 h-10 rounded-xl bg-amber-100 flex items-center justify-center">
                <Pickaxe className="w-5 h-5 text-amber-600" />
              </div>
              <div>
                <h4 className="font-semibold text-primary text-sm">{language === 'pt' ? 'Sondagem' : 'Drilling'}</h4>
                <p className="text-xs text-slate-500">{language === 'pt' ? '36% do total de Subcontratos' : '36% of total Subcontracts'}</p>
              </div>
              <div className="ml-auto flex items-center gap-2">
                <span className="text-lg font-bold text-primary">7.1</span>
                <span className="text-xs text-slate-500">M USD</span>
              </div>
            </div>
            <p className="text-slate-600 text-xs leading-relaxed mb-3">
              {language === 'pt' 
                ? 'Sondagem encerrou em USD 7,1M, ou 36% do total de Subcontratos, com desvio de +6,2% Orçamento e +7,6% vs 2024. Embora com menor peso absoluto do que Man Power, foi a componente com maior pressão relativa face ao plano.'
                : 'Drilling closed at USD 7.1M, or 36% of total Subcontracts, with +6.2% Budget and +7.6% vs 2024 deviation. Although with less absolute weight than Man Power, it was the component with highest relative pressure vs plan.'}
            </p>
            <p className="text-slate-600 text-xs leading-relaxed mb-3">
              {language === 'pt' 
                ? 'Aqui, o desvio deve ser lido sobretudo à luz de campanhas, necessidades técnicas e calendarização de trabalhos, mais do que como custo recorrente. Face ao orçamento, a leitura natural é que houve maior intensidade ou antecipação de actividade relativamente ao previsto. Face a 2024, o aumento mostra uma utilização mais elevada, mas menos acentuada do que em Man Power, o que sugere crescimento relevante sem alteração estrutural tão marcada.'
                : 'Here, the deviation should be read mainly in light of campaigns, technical needs and work scheduling, rather than as recurrent cost. Against budget, the natural reading is higher intensity or activity anticipation vs planned.'}
            </p>
            <p className="text-slate-600 text-xs leading-relaxed mb-3">
              {language === 'pt' 
                ? 'O perfil trimestral confirma maior volatilidade. O 3.º trimestre concentrou o maior custo anual e a diferença entre o trimestre de pico e a mediana é mais visível do que em Man Power. Para a gerência, esta rubrica deve ser tratada como custo táctico de execução técnica, exigindo validação clara entre desvio e entregável: mais metros executados, mais frentes cobertas, maior intensidade de campanha ou alteração de âmbito.'
                : 'The quarterly profile confirms higher volatility. Q3 concentrated the highest annual cost and the difference between peak quarter and median is more visible than in Man Power. For management, this line should be treated as tactical technical execution cost.'}
            </p>
            <div className="bg-white/60 rounded-lg p-3 border border-amber-100">
              <p className="text-xs font-semibold text-slate-700 mb-2">{language === 'pt' ? 'Leitura de Gestão' : 'Management Reading'}</p>
              <ul className="text-xs text-slate-600 space-y-1">
                <li className="flex items-center gap-2">
                  <div className="w-1.5 h-1.5 rounded-full bg-amber-500"></div>
                  {language === 'pt' ? 'Maior pressão relativa face ao plano (+6,2% Orç.)' : 'Highest relative pressure vs plan (+6.2% Budget)'}
                </li>
                <li className="flex items-center gap-2">
                  <div className="w-1.5 h-1.5 rounded-full bg-amber-500"></div>
                  {language === 'pt' ? 'Perfil volátil ligado a campanhas e calendário técnico' : 'Volatile profile linked to campaigns and technical calendar'}
                </li>
                <li className="flex items-center gap-2">
                  <div className="w-1.5 h-1.5 rounded-full bg-amber-500"></div>
                  {language === 'pt' ? 'Validar desvio vs entregável: metros, frentes, intensidade' : 'Validate deviation vs deliverable: meters, fronts, intensity'}
                </li>
              </ul>
            </div>
          </div>

          {/* Leitura Financeira */}
          <div className="bg-gradient-to-r from-slate-50 to-slate-100 rounded-xl p-5 border border-slate-200">
            <h4 className="font-semibold text-primary text-sm mb-3">{language === 'pt' ? 'Leitura Financeira da Rubrica' : 'Financial Reading'}</h4>
            <p className="text-slate-600 text-xs leading-relaxed mb-3">
              {language === 'pt' 
                ? 'O comportamento conjunto das duas componentes sugere três conclusões. Primeiro, o desvio total de Subcontratos não resulta de um único evento, mas de pressão simultânea em capacidade externa operacional e em actividade técnica especializada. Segundo, a composição da rubrica deslocou-se para um perfil em que o custo estrutural de flexibilidade ganhou mais peso do que o inicialmente previsto. Terceiro, a variação face ao homólogo mostra que a utilização de terceiros cresceu mais depressa do que a referência de 2024, o que exige leitura em paralelo com produtividade, headcount interno e necessidade efectiva de suporte externo.'
                : 'The joint behavior of both components suggests three conclusions. First, total Subcontracts deviation does not result from a single event, but from simultaneous pressure on external operational capacity and specialized technical activity. Second, the composition shifted to a profile where structural flexibility cost gained more weight than initially planned. Third, YoY variation shows third-party usage grew faster than 2024 reference.'}
            </p>
          </div>
        </div>
      </ExecutiveSummarySection>

      {/* Comparison Cards - Man Power vs Sondagem */}
      <div className="grid grid-cols-2 gap-6">
        <ComparisonCard 
          title="Man Power"
          data={subcontractsData.manPower}
          prevYearData={prevYearData?.manPower}
          language={language}
          accentColor="blue"
          vsBudget={4.8}
          budgetValue={11.83}
        />
        <ComparisonCard 
          title={language === 'pt' ? 'Sondagem' : 'Drilling'}
          data={subcontractsData.sondagem}
          prevYearData={prevYearData?.sondagem}
          language={language}
          accentColor="amber"
          vsBudget={6.2}
          budgetValue={6.69}
        />
      </div>
    </div>
  );
};

export default SubcontractsAnalysis;
